import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert } from 'antd';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { resetForgottenPassword } from '../services/authService';
import { validatePassword } from '../services/authService';

const { Title } = Typography;

const ResetPassword = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const { token: pathToken } = useParams();
    const searchParams = new URLSearchParams(location.search);
    const queryToken = searchParams.get('token');
    const expirationTime = searchParams.get('expiration_time');
    
    // Get token from either URL path or query parameter
    const token = pathToken || queryToken;
    
    // Check if token has expired
    const isTokenExpired = () => {
        if (!expirationTime) return false;
        const expirationTimestamp = parseInt(expirationTime);
        const currentTimestamp = Date.now();
        return currentTimestamp > expirationTimestamp;
    };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (values) => {
        if (!token) {
            setError(
                'Invalid reset token. Please request a new password reset.'
            );
            return;
        }

        try {
            setLoading(true);
            setError('');
            await resetForgottenPassword(token, values.password);
            setSuccess(true);
        } catch (err) {
            const errorMsg = err.response?.data?.message;
            if (err.response?.status === 404) {
                setError(
                    'This password reset link is invalid or has expired. Please request a new one.'
                );
            } else if (err.response?.status === 400) {
                setError('Invalid password format. Please try again.');
            } else {
                setError(
                    errorMsg || 'Failed to reset password. Please try again.'
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const validatePasswordStrength = (_, value) => {
        if (!value) {
            return Promise.reject('Please input your password!');
        }

        const validation = validatePassword(value);
        if (!validation.isValid) {
            return Promise.reject(validation.feedback.join(', '));
        }

        return Promise.resolve();
    };

    if (!token || isTokenExpired()) {
        return (
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '20px',
                }}
            >
                <Card style={{ width: '100%', maxWidth: 400 }}>
                    <Alert
                        message={isTokenExpired() ? "Expired Reset Link" : "Invalid Reset Link"}
                        description={
                            isTokenExpired() 
                                ? "This password reset link has expired. Please request a new password reset."
                                : "This password reset link is invalid. Please request a new password reset."
                        }
                        type="error"
                        showIcon
                    />
                    <Button
                        type="primary"
                        onClick={() => navigate('/forgot-password')}
                        style={{ marginTop: 16 }}
                        block
                    >
                        Request New Reset Link
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px',
            }}
        >
            <Card style={{ width: '100%', maxWidth: 400 }}>
                {success ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ 
                            fontSize: '48px', 
                            color: '#52c41a', 
                            marginBottom: '16px' 
                        }}>
                            ✓
                        </div>
                        <Title level={3} style={{ 
                            color: '#52c41a', 
                            marginBottom: '16px',
                            fontWeight: 600
                        }}>
                            Password Reset Successful
                        </Title>
                        <p style={{ 
                            color: '#666', 
                            fontSize: '16px', 
                            lineHeight: '1.6',
                            marginBottom: '24px'
                        }}>
                            Your password has been successfully reset.<br />
                            You can now login with your new password.
                        </p>
                        <Button 
                            type="primary" 
                            onClick={() => navigate('/sign-in')}
                            size="large"
                            style={{
                                borderRadius: '6px',
                                height: '44px',
                                fontWeight: '500',
                                fontSize: '15px'
                            }}
                        >
                            Go to Sign In
                        </Button>
                    </div>
                ) : (
                    <>
                        <Title
                            level={2}
                            style={{ textAlign: 'center', marginBottom: 30 }}
                        >
                            Reset Password
                        </Title>

                        {error && (
                            <Alert
                                message={error}
                                type="error"
                                showIcon
                                style={{ marginBottom: 24 }}
                            />
                        )}

                        <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark={false}
                >
                    <Form.Item
                        name="password"
                        label="New Password"
                        rules={[{ validator: validatePasswordStrength }]}
                        hasFeedback
                    >
                        <Input.Password />
                    </Form.Item>

                    <Form.Item
                        name="confirm"
                        label="Confirm New Password"
                        dependencies={['password']}
                        rules={[
                            {
                                required: true,
                                message: 'Please confirm your password!',
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (
                                        !value ||
                                        getFieldValue('password') === value
                                    ) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(
                                        'The passwords do not match!'
                                    );
                                },
                            }),
                        ]}
                        hasFeedback
                    >
                        <Input.Password />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                        >
                            Reset Password
                        </Button>
                    </Form.Item>
                    
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Button 
                            type="link" 
                            onClick={() => navigate('/sign-in')}
                        >
                            Back to Sign In
                        </Button>
                    </div>
                </Form>
                    </>
                )}
            </Card>
        </div>
    );
};

export default ResetPassword;
